import React from "react";
import { Chip, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  Source,
  ScientificSource,
  MateriomFormulationSource,
  SourceTypeValues,
} from "../../types/frontend";
import { styles } from "./styles";

/**
 * AttachmentChipProps interface defines the props for the AttachmentChip component.
 */
interface AttachmentChipProps {
  /** The source to display in the chip */
  source: Source;
  /** Whether the chip is clickable (for opening URLs) */
  clickable?: boolean;
  /** Whether the chip is deletable (shows delete icon) */
  deletable?: boolean;
  /** Function to handle chip deletion */
  onDelete?: (sourceId: string) => void;
  /** Function to handle chip click */
  onClick?: (url: string) => void;
  /** Additional styles to apply to the chip */
  sx?: any;
}

const MAX_TITLE_LENGTH = 30;

/**
 * AttachmentChip Component
 *
 * A reusable chip component for displaying sources in chat messages and input fields.
 * Supports both clickable (for opening URLs) and deletable (for removing from input) modes.
 *
 * @param {AttachmentChipProps} props - Component props
 * @returns {JSX.Element} Rendered AttachmentChip component
 */
const AttachmentChip: React.FC<AttachmentChipProps> = ({
  source,
  clickable = false,
  deletable = false,
  onDelete,
  onClick,
  sx = {},
}) => {
  const theme = useTheme(); // Access the current theme for styling

  /**
   * Get the display text for the source based on its type.
   * @param {Source} source - The source object.
   * @returns {string} - The display text for the source.
   */
  const getSourceDisplayText = (
    source: Source,
    truncate: boolean = true
  ): string => {
    if (source.type === SourceTypeValues.ScientificSource) {
      let title = (source as ScientificSource).title;
      if (truncate && title.length > MAX_TITLE_LENGTH) {
        title = title.slice(0, MAX_TITLE_LENGTH) + "...";
      }
      return title;
    } else if (source.type === SourceTypeValues.MateriomFormulation) {
      return (source as MateriomFormulationSource).name;
    }
    return "Unknown Source";
  };

  /**
   * Get the URL for the source based on its type.
   * @param {Source} source - The source object.
   * @returns {string} - The URL for the source.
   */
  const getSourceUrl = (source: Source): string => {
    if (source.type === SourceTypeValues.ScientificSource) {
      return (source as ScientificSource).url;
    } else if (source.type === SourceTypeValues.MateriomFormulation) {
      return (source as MateriomFormulationSource).url;
    }
    return "";
  };

  /**
   * Handle the click event for the chip.
   * Opens the source URL if the chip is clickable.
   */
  const handleClick = () => {
    if (clickable && onClick) {
      const url = getSourceUrl(source);
      onClick(url);
    }
  };

  /**
   * Handle the delete event for the chip.
   * Calls the onDelete function with the source ID if the chip is deletable.
   */
  const handleDelete = () => {
    if (deletable && onDelete) {
      onDelete(source.id);
    }
  };

  // Define styles for the chip based on its state (clickable, deletable)
  const baseStyles = styles.attachmentChip(theme);
  const clickableStyles = clickable
    ? styles.attachmentChipClickable(theme)
    : {};
  const deletableStyles = deletable
    ? styles.attachmentChipDeletable(theme)
    : {};

  // Create the chip component
  const attachmentChip = (
    <Chip
      key={source.id}
      label={getSourceDisplayText(source, true)}
      size="small"
      clickable={clickable}
      deleteIcon={deletable ? <CloseIcon /> : undefined}
      onClick={handleClick}
      onDelete={deletable ? handleDelete : undefined}
      sx={{
        ...baseStyles,
        ...clickableStyles,
        ...deletableStyles,
        ...sx,
      }}
    />
  );

  // Only show tooltip if the full text is longer than the max length
  const shouldShowTooltip =
    getSourceDisplayText(source, false).length > MAX_TITLE_LENGTH;

  return shouldShowTooltip ? (
    <Tooltip title={getSourceDisplayText(source, false)} placement="top">
      {attachmentChip}
    </Tooltip>
  ) : (
    attachmentChip
  );
};

export default AttachmentChip;
